import React from 'react';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import MovieOutlinedIcon from '@mui/icons-material/MovieOutlined';
import TheatersOutlinedIcon from '@mui/icons-material/TheatersOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import MusicNoteOutlinedIcon from '@mui/icons-material/MusicNoteOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import HowToVoteOutlinedIcon from '@mui/icons-material/HowToVoteOutlined';
import FamilyRestroomOutlinedIcon from '@mui/icons-material/FamilyRestroomOutlined';
import { formatPhaseLabel, phaseOptionsForCampaign } from './phaseConfig';
import type { ContestWizardState } from './types';

export interface ContestSelectOption {
    value: string;
    label: string;
    subtitle: string;
    icon: React.ReactNode;
}

const durationOptions: ContestSelectOption[] = [1, 2, 3, 5, 10, 15, 30, 60].map(m => ({
    value: String(m),
    label: `Less than ${m} min`,
    subtitle:
        m <= 3 ? 'Short-form social content' : m <= 15 ? 'Standard contest entries' : 'Extended or feature-length',
    icon: <TimerOutlinedIcon />,
}));

const formatOptions: ContestSelectOption[] = [
    {
        value: 'video',
        label: 'Video',
        subtitle: 'Filmed motion content',
        icon: <VideocamOutlinedIcon />,
    },
    {
        value: 'act',
        label: 'Act',
        subtitle: 'Live or recorded performance',
        icon: <TheatersOutlinedIcon />,
    },
];

const mediaOptions: ContestSelectOption[] = [
    { value: 'video', label: 'Video', subtitle: 'MP4, MOV, and similar', icon: <MovieOutlinedIcon /> },
    { value: 'audio', label: 'Audio', subtitle: 'Music, voice, podcasts', icon: <MusicNoteOutlinedIcon /> },
    { value: 'picture', label: 'Picture', subtitle: 'Still photography', icon: <ImageOutlinedIcon /> },
    {
        value: 'document',
        label: 'Document',
        subtitle: 'Scripts, treatments, PDFs',
        icon: <DescriptionOutlinedIcon />,
    },
];

const votingOptions: ContestSelectOption[] = [
    { value: 'daily', label: 'Daily', subtitle: 'Votes reset every day', icon: <HowToVoteOutlinedIcon /> },
    { value: 'weekly', label: 'Weekly', subtitle: 'Votes reset each week', icon: <HowToVoteOutlinedIcon /> },
    { value: 'hourly', label: 'Hourly', subtitle: 'High-frequency voting windows', icon: <HowToVoteOutlinedIcon /> },
    { value: 'monthly', label: 'Monthly', subtitle: 'Longer audience cycles', icon: <HowToVoteOutlinedIcon /> },
    { value: 'one-off', label: 'One-off', subtitle: 'Single voting period', icon: <HowToVoteOutlinedIcon /> },
];

const ratingOptions: ContestSelectOption[] = [
    { value: 'G', label: 'G', subtitle: 'General audiences', icon: <FamilyRestroomOutlinedIcon /> },
    { value: 'PG', label: 'PG', subtitle: 'Parental guidance suggested', icon: <FamilyRestroomOutlinedIcon /> },
    { value: 'PG-13', label: 'PG-13', subtitle: 'Teens and older', icon: <FamilyRestroomOutlinedIcon /> },
    { value: 'R', label: 'R', subtitle: 'Restricted — 17+', icon: <FamilyRestroomOutlinedIcon /> },
    { value: 'NC-17', label: 'NC-17', subtitle: 'Adults only', icon: <FamilyRestroomOutlinedIcon /> },
];

const phaseSubtitles: Record<string, string> = {
    preparation: 'Setup before creators engage',
    ideation: 'Concept and pitch development',
    pitch: 'Present ideas for approval',
    submission: 'Creators upload entries',
    production: 'Create and deliver work',
    judging: 'Jury review and scoring',
    vote: 'Audience voting period',
    event: 'Live or showcase moment',
    closed: 'Campaign wrapped up',
};

const phaseIcons: Record<string, React.ReactNode> = {
    preparation: <TimerOutlinedIcon />,
    ideation: <DescriptionOutlinedIcon />,
    pitch: <TheatersOutlinedIcon />,
    submission: <VideocamOutlinedIcon />,
    production: <MovieOutlinedIcon />,
    judging: <FamilyRestroomOutlinedIcon />,
    vote: <HowToVoteOutlinedIcon />,
    event: <TheatersOutlinedIcon />,
    closed: <TimerOutlinedIcon />,
};

export function getDurationSelectOptions(): ContestSelectOption[] {
    return durationOptions;
}

export function getFormatSelectOptions(): ContestSelectOption[] {
    return formatOptions;
}

export function getMediaSelectOptions(): ContestSelectOption[] {
    return mediaOptions;
}

export function getVotingSelectOptions(): ContestSelectOption[] {
    return votingOptions;
}

export function getRatingSelectOptions(): ContestSelectOption[] {
    return ratingOptions;
}

export function getPhaseSelectOptions(submissionState: ContestWizardState['submission_state']): ContestSelectOption[] {
    return phaseOptionsForCampaign(submissionState).map(phase => ({
        value: phase,
        label: formatPhaseLabel(phase),
        subtitle: phaseSubtitles[phase] || 'Campaign phase',
        icon: phaseIcons[phase] ?? <TimerOutlinedIcon />,
    }));
}

export function getPhaseSelectOptionsFromValues(phases: string[]): ContestSelectOption[] {
    return phases.map(phase => ({
        value: phase,
        label: formatPhaseLabel(phase),
        subtitle: phaseSubtitles[phase] || 'Campaign phase',
        icon: phaseIcons[phase] ?? <TimerOutlinedIcon />,
    }));
}
